import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { useSellerSignupData } from '@/hooks/useSellerSignupData';
import { Toast } from '@/components/common/Toast';

// Desktop components following SRP
import { SellerSignupHeader } from '@/components/desktop/auth/seller/SellerSignupHeader';
import { BusinessInfoForm } from '@/components/desktop/auth/seller/BusinessInfoForm';
import { BusinessAgreements } from '@/components/desktop/auth/seller/BusinessAgreements';

interface BusinessInfo {
  companyName: string;
  representative: string;
  businessNumber: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
}

interface Agreement {
  terms: boolean;
  privacy: boolean;
  marketing: boolean;
}

export function DesktopSellerBusinessInfoPage() {
  const navigate = useNavigate();
  const { businessInfo, agreements, updateBusinessInfo, updateAgreements } = useSellerSignupData();

  const [formData, setFormData] = useState<BusinessInfo>({
    companyName: businessInfo.companyName || '',
    representative: businessInfo.representative || '',
    businessNumber: businessInfo.businessNumber || '',
    bankName: businessInfo.bankName || '',
    accountNumber: businessInfo.accountNumber || '',
    accountHolder: businessInfo.accountHolder || ''
  });

  const [agreementData, setAgreementData] = useState<Agreement>({
    terms: agreements.terms || false,
    privacy: agreements.privacy || false,
    marketing: agreements.marketing || false
  });

  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  const handleFormChange = (field: keyof BusinessInfo, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // localStorage에 자동 저장
    updateBusinessInfo({ [field]: value });
  };

  const handleAgreementChange = (field: keyof Agreement, checked: boolean) => {
    setAgreementData(prev => ({
      ...prev,
      [field]: checked
    }));
    
    // localStorage에 자동 저장
    updateAgreements({ [field]: checked });
  };

  const handleAllAgreementChange = (checked: boolean) => {
    const newAgreements = {
      terms: checked,
      privacy: checked,
      marketing: checked
    };
    setAgreementData(newAgreements);
    updateAgreements(newAgreements);
  };

  const isFormValid = () => {
    return formData.companyName &&
           formData.representative &&
           formData.businessNumber &&
           formData.bankName &&
           formData.accountNumber &&
           formData.accountHolder &&
           agreementData.terms &&
           agreementData.privacy;
  };

  const handleNext = () => {
    if (!isFormValid()) {
      setToast({ show: true, message: '모든 필수 항목을 입력하고 약관에 동의해주세요' });
      return;
    }

    // 다음 단계로 이동 - 매장 정보 입력
    navigate('/signup/seller/store');
  };

  return (
    <DesktopLayout>
      <div className="min-h-[80vh] flex items-center justify-center py-12">
        <div className="w-full max-w-6xl bg-white rounded-2xl shadow-lg border border-gray-200 px-12 py-16">
          
          {/* Header Section */}
          <SellerSignupHeader 
            title="사업자 정보 입력"
            subtitle="사업자 등록 정보와 정산 계좌를 입력해주세요"
            currentStep={2}
            totalSteps={3}
            className="mb-12" 
          />
          
          {/* Form Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* 좌측: 사업자 정보 */}
            <div className="lg:col-span-2">
              <BusinessInfoForm
                formData={formData}
                onFormChange={handleFormChange}
              />
            </div>
            
            {/* 우측: 약관 동의 */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <BusinessAgreements
                  agreementData={agreementData}
                  onAgreementChange={handleAgreementChange}
                  onAllAgreementChange={handleAllAgreementChange}
                />
                
                {/* 다음 단계 버튼 */}
                <div className="mt-8">
                  <button
                    onClick={handleNext}
                    disabled={!isFormValid()}
                    className={`w-full h-[56px] font-semibold text-[20px] rounded-xl transition-colors ${
                      isFormValid() 
                        ? 'bg-[#51C854] text-white hover:bg-[#46B649] active:scale-[0.98]' 
                        : 'bg-[#DEDEDE] text-[#999999] cursor-not-allowed'
                    }`}
                  >
                    다음 단계
                  </button>
                </div>

                {/* 도움말 */}
                <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="text-[16px] font-medium text-yellow-700 mb-2">
                    💡 안내사항
                  </h4>
                  <ul className="space-y-1 text-[14px] text-yellow-600">
                    <li>• 사업자 등록증 내용과 일치해야 합니다</li>
                    <li>• 정산 계좌는 사업자명으로 개설된 계좌여야 합니다</li>
                    <li>• 입력된 정보는 심사를 위해 확인됩니다</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 토스트 메시지 */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </DesktopLayout>
  );
}